import { WarehouseItemWorkRequestKind } from "@redotech/crdb-enums/fulfillment/warehouse-item-work-request-kind";
import { t } from "@redotech/redo-i18n";
import CallBellIcon from "@redotech/redo-icons/call-bell_filled.svg";
import { Step } from "@redotech/redo-return-core-common/return-flow";
import { makeServiceRequestFromModel } from "@redotech/redo-return-core-common/return-flow-step-from-model";
import {
  isMakeServiceRequestStepValid,
  MakeServiceRequestState,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { produce } from "immer";
import * as React from "react";
import { StepType, StepTypeDetailsProps } from "../step";

export const MAKE_SERVICE_REQUEST = (
  Details: React.ComponentType<StepTypeDetailsProps<MakeServiceRequestState>>,
): StepType<MakeServiceRequestState, Step.MakeServiceRequest> => ({
  Details: (props) => React.createElement(Details, props),
  customTitle: (state) => state.customTitle,
  description: (state) => getWorkRequestKindLabel(state.requestKind),
  downstream(state) {
    return state.next ? [{ id: state.next }] : [];
  },
  empty: {
    requestKind: WarehouseItemWorkRequestKind.Photo,
    scope: "return_item",
  },
  fromModel: (model, id) => makeServiceRequestFromModel(model, id),
  Icon: CallBellIcon,
  // Matches the palette's Service requests section and the other silent
  // action step, PROCESSING_ACTION.
  iconColor: "success",
  layout: () => BlockLayout.FULL,
  maxDownstream: () => 1,
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  stepDeleted(state, stepId) {
    return produce(state, (draft) => {
      if (draft.next === stepId) {
        draft.next = undefined;
      }
    });
  },
  title: STEP_TYPE_NAMES[Step.MAKE_SERVICE_REQUEST],
  toModel(state, id) {
    return {
      type: Step.MAKE_SERVICE_REQUEST,
      customTitle: state.customTitle,
      requestKind: state.requestKind,
      photoInstructionsId: state.photoInstructionsId ?? null,
      next: state.next ? id(state.next) : null,
      scope: state.scope,
    };
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(
        `Invalid handle index ${handleIndex} for make-service-request step`,
      );
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  valid: (state) => isMakeServiceRequestStepValid(state).valid,
});

function getWorkRequestKindLabel(kind: WarehouseItemWorkRequestKind): string {
  switch (kind) {
    case WarehouseItemWorkRequestKind.Photo:
      return t("Photo request");
    case WarehouseItemWorkRequestKind.Qa:
      return t("QA request", { context: "quality assurance" });
  }
}
